<template>
  <main-drawer :title="$tc('seminar.register_participant')" @close="$emit('close')">
    <main-select
      v-if="user.isAdmin"
      v-model="form.clubId"
      :label="$tc('general.club')"
      :options="clubOptions"
      @input="onClubChange"
    />

    <main-select
      v-model="form.selectedIds"
      :label="$tc('seminar.select_members')"
      :options="memberOptions"
      :disabled="!effectiveClub"
      multiple
      filterable
    />

    <div v-if="selectedForPositions.length" class="selected-list">
      <h4>{{ $tc('seminar.assign_positions_per_participant') }}</h4>
      <div v-for="item in selectedForPositions" :key="item.sourceKey" class="participant-row">
        <span class="name">{{ getPersonName(item.sourceKey) }}</span>
        <main-select
          v-model="item.positions"
          :label="''"
          :options="positionOptions"
          :placeholder="$tc('seminar.assign_positions')"
          multiple
          filterable
          class="participant-positions-select"
        />
      </div>
    </div>

    <template v-slot:footer>
      <div class="flex-buttons">
        <el-button @click="$emit('close')">{{ $tc('general.cancel') }}</el-button>
        <el-button type="primary" :disabled="!canSave" @click="save()">{{ $tc('general.save') }}</el-button>
      </div>
    </template>
  </main-drawer>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'
import { SEMINAR_POSITIONS } from './seminarPositions'

export default {
  props: {
    seminar: { type: Object, required: true },
    club: { type: Object, default: null },
    participants: { type: Array, default: () => [] }
  },

  data () {
    return {
      form: {
        clubId: null,
        selectedIds: []
      },
      selectedForPositions: [],
      clubWithMembers: null,
      SEMINAR_POSITIONS
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    },
    effectiveClub () {
      if (this.user.isAdmin && this.form.clubId) {
        return this.clubWithMembers || this.$store.getters['clubs/get'](this.form.clubId)
      }
      return this.club || this.user?.club || this.clubWithMembers
    },
    members () {
      const club = this.effectiveClub
      if (!club?.members) return []
      return club.members.filter(m => m.isActive)
    },
    coaches () {
      const club = this.effectiveClub
      if (!club?.coaches) return []
      return club.coaches.filter(c => c.isActive)
    },
    officials () {
      const club = this.effectiveClub
      if (!club?.officials) return []
      return club.officials.filter(o => o.isActive)
    },
    referees () {
      const club = this.effectiveClub
      if (!club?.referees) return []
      return club.referees.filter(r => r.isActive)
    },
    registeredSourceKeys () {
      const keys = new Set()
      this.participants.forEach(p => {
        if (p.memberId) keys.add(`member:${p.memberId}`)
        if (p.coachId) keys.add(`coach:${p.coachId}`)
        if (p.officialId) keys.add(`official:${p.officialId}`)
        if (p.refereeId) keys.add(`referee:${p.refereeId}`)
      })
      return keys
    },
    memberOptions () {
      const opts = []
      const coachLabel = this.$tc('seminar.Coach')
      const officialLabel = this.$tc('seminar.Sports Official')
      const judgeLabel = this.$tc('seminar.Judge')
      const alreadyRegisteredLabel = this.$tc('seminar.already_registered')
      this.members.forEach(m => {
        const id = `member:${m.id}`
        const baseText = `${m.firstName} ${m.lastName}`
        const isRegistered = this.registeredSourceKeys.has(id)
        opts.push({
          id,
          text: isRegistered ? `${baseText} (${alreadyRegisteredLabel})` : baseText,
          disabled: isRegistered
        })
      })
      this.coaches.forEach(c => {
        const id = `coach:${c.id}`
        const baseText = `${c.firstName} ${c.lastName} (${coachLabel})`
        const isRegistered = this.registeredSourceKeys.has(id)
        opts.push({
          id,
          text: isRegistered ? `${baseText} - ${alreadyRegisteredLabel}` : baseText,
          disabled: isRegistered
        })
      })
      this.officials.forEach(o => {
        const id = `official:${o.id}`
        const baseText = `${o.firstName} ${o.lastName} (${officialLabel})`
        const isRegistered = this.registeredSourceKeys.has(id)
        opts.push({
          id,
          text: isRegistered ? `${baseText} - ${alreadyRegisteredLabel}` : baseText,
          disabled: isRegistered
        })
      })
      this.referees.forEach(r => {
        const id = `referee:${r.id}`
        const baseText = `${r.firstName} ${r.lastName} (${judgeLabel})`
        const isRegistered = this.registeredSourceKeys.has(id)
        opts.push({
          id,
          text: isRegistered ? `${baseText} - ${alreadyRegisteredLabel}` : baseText,
          disabled: isRegistered
        })
      })
      return opts
    },
    clubOptions () {
      const list = this.$store.getters['clubs/list'] || []
      return list.filter(c => !c.isBlocked && !c.isDeleted).map(c => ({ id: c.id, text: c.name }))
    },
    positionOptions () {
      return this.SEMINAR_POSITIONS.map(pos => ({
        id: pos,
        text: this.getPositionLabel(pos)
      }))
    },
    canSave () {
      if (!this.form.selectedIds.length || !this.effectiveClub) return false
      return this.selectedForPositions.every(p => p.positions.length > 0)
    }
  },

  watch: {
    'form.selectedIds' (ids) {
      const current = this.selectedForPositions.map(s => s.sourceKey)
      const added = ids.filter(id => !current.includes(id))
      const removed = current.filter(id => !ids.includes(id))
      added.forEach(sourceKey => {
        this.selectedForPositions.push({ sourceKey, positions: [] })
      })
      this.selectedForPositions = this.selectedForPositions.filter(s => !removed.includes(s.sourceKey))
    },
    club: {
      immediate: true,
      handler (c) {
        if (c && !this.form.clubId && !this.user.isAdmin) {
          this.form.clubId = c.id
          this.loadClubMembers(c.id)
        }
      }
    }
  },

  created () {
    if (this.user.isAdmin && this.clubOptions.length) {
      this.form.clubId = this.club?.id || this.user?.club?.id || this.clubOptions[0]?.id
    } else if (this.user?.club) {
      this.form.clubId = this.user.club.id
    }
    this.loadClubMembers(this.form.clubId)
    this.$store.dispatch('clubs/load')
  },

  methods: {
    onClubChange () {
      this.selectedForPositions = []
      this.form.selectedIds = []
      this.loadClubMembers(this.form.clubId)
    },
    async loadClubMembers (clubId) {
      if (!clubId) return
      try {
        await this.$store.dispatch('clubs/get', clubId)
        this.clubWithMembers = this.$store.getters['clubs/get'](clubId)
      } catch (e) {
        console.error(e)
      }
    },
    getPositionLabel (pos) {
      return this.$tc('seminar.' + pos)
    },
    getPersonName (sourceKey) {
      if (!sourceKey) return ''
      const [type, idStr] = sourceKey.split(':')
      const id = parseInt(idStr, 10)
      if (type === 'member') {
        const m = this.members.find(m => m.id === id)
        return m ? `${m.firstName} ${m.lastName}` : ''
      }
      if (type === 'coach') {
        const c = this.coaches.find(c => c.id === id)
        return c ? `${c.firstName} ${c.lastName}` : ''
      }
      if (type === 'official') {
        const o = this.officials.find(o => o.id === id)
        return o ? `${o.firstName} ${o.lastName}` : ''
      }
      if (type === 'referee') {
        const r = this.referees.find(r => r.id === id)
        return r ? `${r.firstName} ${r.lastName}` : ''
      }
      return ''
    },
    buildParticipantPayload (item) {
      const [type, idStr] = item.sourceKey.split(':')
      const id = parseInt(idStr, 10)
      const base = { clubId: this.effectiveClub.id, positions: item.positions }
      if (type === 'member') return { ...base, memberId: id }
      if (type === 'coach') return { ...base, coachId: id }
      if (type === 'official') return { ...base, officialId: id }
      if (type === 'referee') return { ...base, refereeId: id }
      return null
    },
    save () {
      if (!this.canSave) return
      const participants = this.selectedForPositions
        .filter(p => p.positions.length > 0)
        .map(p => this.buildParticipantPayload(p))
        .filter(Boolean)
      if (!participants.length) return
      this.$store.dispatch('seminars/registerParticipants', {
        id: this.seminar.id,
        participants
      }).then(() => {
        this.$message.success(this.$tc('messages.success_created'))
        this.$emit('saved')
      }).catch(errorHandler)
    }
  }
}
</script>

<style scoped>
.selected-list { margin-top: 20px; }
.participant-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.participant-row .name { flex: 1; min-width: 0; }
.participant-row .participant-positions-select {
  flex: 1;
  min-width: 200px;
}
</style>
